import {useCallback} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useWebSocket} from "../../contexts/WebSocketContext";
import {useImproveAxios} from "../../hooks/useImproveAxios";
import {getImproveAPI, type LessonVideoProgressMessage} from "../gen";
import {getLessonForCourseQueryKey} from "./courses";

/** Client → server event; matches improve-api `LESSON_WEBSOCKET_EVENTS.VIDEO_PROGRESS` (not in OpenAPI). */
export const LESSON_VIDEO_PROGRESS_SOCKET_EVENT = "lesson.video.progress" as const;

export interface FlushLessonVideoProgressParams {
	enrollmentId: number;
	progress: number;
	courseSlug: string;
	lessonSlug: string;
}

/**
 * Persist last known watch position: WebSocket when available, else PUT /lessons/.../video-time
 * (same as real-time path). Invalidates the lesson query so the next load shows videoTime.
 * Call on unmount (e.g. navigate away).
 */
export const useFlushLessonVideoProgress = () => {
	const {socket} = useWebSocket();
	const queryClient = useQueryClient();
	const axiosConfig = useImproveAxios();

	return useCallback(
		(params: FlushLessonVideoProgressParams): void => {
			if (!params.enrollmentId || params.progress == null) return;
			const invalidate = (): void => {
				void queryClient.invalidateQueries({
					queryKey: getLessonForCourseQueryKey(params.courseSlug, params.lessonSlug)
				});
			};

			if (socket?.emit) {
				const message: LessonVideoProgressMessage = {
					enrollmentId: params.enrollmentId,
					progress: params.progress
				};
				socket.emit(LESSON_VIDEO_PROGRESS_SOCKET_EVENT, message);
				invalidate();
				return;
			}

			const {lessonsControllerSaveVideoTime} = getImproveAPI();
			const time = Math.round(params.progress);
			void lessonsControllerSaveVideoTime(params.enrollmentId, {time}, axiosConfig)
				.then(invalidate)
				.catch(() => {
					/* best effort on unmount */
				});
		},
		[socket, queryClient, axiosConfig]
	);
};
